import React, {useEffect, useLayoutEffect, useRef} from 'react';
import { createPortal } from 'react-dom';
import { getOrCreateOrigin, positionOriginAtElement } from '../../helpers/uiHelpers';

// Lightweight Popover — anchored element that closes when clicking outside
export default function Popover({ isOpen, onClose, children, style = {}, x = null, y = null, className = '', id, triggerRef = null, placement = 'bottom' }) {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const useOriginPositioning = Boolean(triggerRef?.current && containerRef.current);

  if (typeof document !== 'undefined' && !containerRef.current) {
    containerRef.current = getOrCreateOrigin({ className: 'popover-origin', preferSibling: '.context-origin' });
  }

  useEffect(() => {
    if (!isOpen) return;
    const onDocMouse = (e) => {
      try {
        if (!ref.current) return;
        // If the click is inside the popover, keep it open
        if (ref.current.contains(e.target)) return;
        // If the click is inside any context menu / origin (portal), keep open
        if (e.target && e.target.closest) {
          const insideContext = e.target.closest('.contextMenu') || e.target.closest('.context-origin');
          if (insideContext) return;
        }
        // otherwise close
        onClose?.();
      } catch (err) {}
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('mousedown', onDocMouse);
    window.addEventListener('touchstart', onDocMouse);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDocMouse);
      window.removeEventListener('touchstart', onDocMouse);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef?.current || !containerRef.current) return;

    const applyWrapper = () => {
      positionOriginAtElement(containerRef.current, triggerRef.current);
    };

    applyWrapper();
    window.addEventListener('resize', applyWrapper);
    window.addEventListener('scroll', applyWrapper, true);
    return () => {
      window.removeEventListener('resize', applyWrapper);
      window.removeEventListener('scroll', applyWrapper, true);
    };
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  const mergedStyle = { ...(style || {}) };
  if (Number.isFinite(x)) mergedStyle.left = x;
  if (Number.isFinite(y)) mergedStyle.top = y;

  const {
    left: _left,
    top: _top,
    right: _right,
    bottom: _bottom,
    transform: _transform,
    ...nonCoordinateStyle
  } = mergedStyle;

  const resolvedStyle = useOriginPositioning
    ? { position: 'absolute', ...nonCoordinateStyle }
    : { position: 'fixed', ...mergedStyle };

  const placementClasses = useOriginPositioning
    ? String(placement || '').toLowerCase()
    : '';

  const popoverNode = (
    <div
      ref={ref}
      id={id}
      className={`popover ${className} ${placementClasses}`.trim()}
      style={resolvedStyle}
    >
      {children}
    </div>
  );

  if (useOriginPositioning && typeof document !== 'undefined') {
    return createPortal(popoverNode, containerRef.current);
  }

  return popoverNode;
}
